import React from 'react';
import { subscribeToAnswerStream } from './events';
import { AnswerStreamSubject } from './context';
import {
  createInitialStreamState,
  matchesAnswerStreamTarget,
  reduceAnswerStreamEvent,
  StreamState,
} from './stream-state';

const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

export function useLLMPlaygroundStream({
  subject,
  questionSlug,
  persistedOutput,
  persistedReview,
  persistedIsCorrect,
  submitAnswer,
}: {
  subject: AnswerStreamSubject | null;
  questionSlug: string;
  persistedOutput: string | null;
  persistedReview: string | null;
  persistedIsCorrect: boolean | null;
  submitAnswer: () => Promise<void>;
}) {
  const [state, setState] = React.useState<StreamState>(() =>
    createInitialStreamState({
      output: persistedOutput,
      review: persistedReview,
      isCorrect: persistedIsCorrect,
    })
  );
  const persistedRef = React.useRef({
    output: persistedOutput,
    review: persistedReview,
    isCorrect: persistedIsCorrect,
  });
  const timeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    persistedRef.current = {
      output: persistedOutput,
      review: persistedReview,
      isCorrect: persistedIsCorrect,
    };
  }, [persistedOutput, persistedReview, persistedIsCorrect]);

  const clearStreamTimeout = React.useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetStreamTimeout = React.useCallback(() => {
    clearStreamTimeout();
    timeoutRef.current = window.setTimeout(() => {
      setState((current) => ({
        ...current,
        status: 'error',
        error: 'The AI response timed out. Please try again.',
      }));
    }, STREAM_TIMEOUT_MS);
  }, [clearStreamTimeout]);

  React.useEffect(() => {
    if (
      (state.status === 'awaiting' || state.status === 'streaming') &&
      persistedOutput !== null &&
      persistedReview !== null
    ) {
      clearStreamTimeout();
      setState({
        status: 'complete',
        streamId: null,
        output: persistedOutput,
        review: persistedReview,
        isCorrect: persistedIsCorrect,
        error: null,
      });
    }
  }, [
    state.status,
    persistedOutput,
    persistedReview,
    persistedIsCorrect,
    clearStreamTimeout,
  ]);

  React.useEffect(() => clearStreamTimeout, [clearStreamTimeout]);

  React.useEffect(() => {
    setState({
      status: 'idle',
      streamId: null,
      output: persistedRef.current.output,
      review: persistedRef.current.review,
      isCorrect: persistedRef.current.isCorrect,
      error: null,
    });
    clearStreamTimeout();
  }, [
    questionSlug,
    subject?.subjectType,
    subject?.subjectId,
    clearStreamTimeout,
  ]);

  React.useEffect(() => {
    if (!subject) return;

    return subscribeToAnswerStream((event) => {
      if (!matchesAnswerStreamTarget(event, subject, questionSlug)) {
        return;
      }

      if (event.type === 'answer-stream-start') {
        resetStreamTimeout();
      } else if (
        event.type === 'answer-stream-chunk' ||
        (event.type === 'answer-stream-state' &&
          (event.status === 'streaming' || event.status === 'pending'))
      ) {
        resetStreamTimeout();
      } else {
        clearStreamTimeout();
      }

      setState((current) => reduceAnswerStreamEvent(current, event));
    });
  }, [
    subject,
    questionSlug,
    resetStreamTimeout,
    clearStreamTimeout,
  ]);

  const submit = React.useCallback(async () => {
    if (!subject || ['submitting', 'awaiting', 'streaming'].includes(state.status)) {
      return;
    }
    setState({
      status: 'submitting',
      streamId: null,
      output: null,
      review: null,
      isCorrect: null,
      error: null,
    });
    resetStreamTimeout();
    try {
      await submitAnswer();
      setState((current) =>
        current.status === 'submitting'
          ? { ...current, status: 'awaiting' }
          : current
      );
    } catch (error) {
      clearStreamTimeout();
      setState((current) => ({
        ...current,
        status: 'error',
        error:
          error instanceof Error
            ? error.message
            : 'The answer could not be submitted.',
      }));
    }
  }, [subject, state.status, submitAnswer, resetStreamTimeout, clearStreamTimeout]);

  const reset = React.useCallback(() => {
    clearStreamTimeout();
    setState({
      status: 'idle',
      streamId: null,
      output: null,
      review: null,
      isCorrect: null,
      error: null,
    });
  }, [clearStreamTimeout]);

  return {
    ...state,
    isBusy: ['submitting', 'awaiting', 'streaming'].includes(state.status),
    submit,
    reset,
  };
}
